import { tr } from '../i18n';
import type { ChatService, SendOptions, StreamChunk, UploadOptions } from '../types';

const MOCK_UPLOAD_STEPS = [20, 45, 70, 90, 100];
const MOCK_UPLOAD_STEP_MS = 200;

let mockUploadSeq = 0;

// Neutral placeholder — must never read as a real AI answer (the mock is
// swapped for the real orchestrator by initAiCio before users reach it).
function cannedReply(): string {
  return tr(
    'message.canned',
    `I'm just getting things ready — please send your message again in a moment.`,
  );
}

/**
 * Splits text into word-ish chunks for a typewriter effect, preserving
 * whitespace so re-joining reproduces the source markdown.
 */
function chunkText(text: string): string[] {
  return text.match(/\S+\s*/g) ?? [text];
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const id = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(id);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}

/**
 * A local, backend-free {@link ChatService}. Streams a canned reply token by
 * token to exercise the streaming UI. Swap for a real service via
 * `setChatService` when the backend is available.
 */
export function createMockChatService(): ChatService {
  return {
    async *streamReply(_prompt: string, options: SendOptions): AsyncIterable<StreamChunk> {
      const chunks = chunkText(cannedReply());
      // Small initial "thinking" pause.
      await delay(250, options.signal);
      for (const chunk of chunks) {
        await delay(18, options.signal);
        yield { delta: chunk, done: false };
      }
      yield { delta: '', done: true };
    },

    async uploadFile(_file: File, options?: UploadOptions): Promise<{ fileRef: string }> {
      for (const percent of MOCK_UPLOAD_STEPS) {
        await delay(MOCK_UPLOAD_STEP_MS, options?.signal);
        options?.onProgress?.(percent);
      }
      return { fileRef: `mock-file-${(++mockUploadSeq).toString()}` };
    },

    // Nothing is stored outside the browser, so there is nothing to erase.
    deleteSession: () => Promise.resolve('deleted'),
    clearHistory: () => Promise.resolve('deleted'),
  };
}
